import {
  BulkUpdateContainerVisitCommand,
  CargoDataV2,
  CarrierType,
  TransportDataV2,
} from '@planning/app/api'
import { CargoSubFormStore } from '@planning/pages/VesselVisit/Components/BulkEdit/CargoSubForm/CargoSubFormStore'
import { InboundSubFormStore } from '@planning/pages/VesselVisit/Components/BulkEdit/InboundSubForm/InboundSubFormStore'
import { LogisticsSubFormStore } from '@planning/pages/VesselVisit/Components/BulkEdit/LogisticsSubForm/LogisticsSubFormStore'
import { OutboundSubFormStore } from '@planning/pages/VesselVisit/Components/BulkEdit/OutboundSubForm/OutboundSubFormStore'

import { containerService } from '@planning/services'
import { removeEmptyOptionalFields } from '@planning/utils'
import { FormStore } from '@tom-ui/utils'
import { computed, makeObservable, observable } from 'mobx'
import { BulkEditSelectionDto } from '../tableConfigs/useRailVisitTableActions'

export type RailVisitBulkEditFormStoreType = {
  inboundTransport: InboundSubFormStore['data']
  outboundTransport: OutboundSubFormStore['data']
  logistics: LogisticsSubFormStore['data']
  cargo: CargoSubFormStore['data']
}

export class RailVisitBulkEditFormStore extends FormStore<RailVisitBulkEditFormStoreType> {
  inboundTransport: InboundSubFormStore
  outboundTransport: OutboundSubFormStore
  logistics: LogisticsSubFormStore
  cargo: CargoSubFormStore

  constructor() {
    super()

    makeObservable(this, {
      inboundTransport: observable,
      outboundTransport: observable,
      logistics: observable,
      cargo: observable,
      hasChanges: computed,
    })

    this.inboundTransport = new InboundSubFormStore()
    this.outboundTransport = new OutboundSubFormStore()
    this.logistics = new LogisticsSubFormStore()
    this.cargo = new CargoSubFormStore()

    this.register('inboundTransport', this.inboundTransport)
    this.register('outboundTransport', this.outboundTransport)
    this.register('logistics', this.logistics)
    this.register('cargo', this.cargo)
  }

  get data(): RailVisitBulkEditFormStoreType {
    return {
      inboundTransport: this.inboundTransport.data,
      outboundTransport: this.outboundTransport.data,
      logistics: this.logistics.data,
      cargo: this.cargo.data,
    }
  }

  get hasChanges(): boolean {
    const allDataValues = [
      ...Object.values(this.data.inboundTransport),
      ...Object.values(this.data.outboundTransport),
      ...Object.values(this.data.logistics),
      ...Object.values(this.data.cargo),
    ]

    return allDataValues.some(value => value !== undefined)
  }

  async submit(bulkEditData: BulkEditSelectionDto[]) {
    try {
      const formData = this.data
      const command = this._mapDataToCommand(formData, bulkEditData)
      await containerService.bulkEdit(command)
    } catch (error) {
      console.log(error)
      throw error
    }
  }

  private _mapInboundTransport(
    inboundData: InboundSubFormStore['data'],
    sharedData: Partial<TransportDataV2>,
  ): TransportDataV2 {
    const carrierVisitValue =
      inboundData.carrierVisit === null ? null : inboundData.carrierVisit?.id

    const carrierType = inboundData.carrierType ?? undefined
    const carrierTypeValue =
      inboundData.carrierVisit === null && !carrierType ? CarrierType.Universal : carrierType

    return removeEmptyOptionalFields({
      ...sharedData,
      carrierVisitId: carrierVisitValue,
      carrierType: carrierTypeValue,
      isApproved: inboundData.approvalForDischarge,
    })
  }

  private _mapOutboundTransport(
    outboundData: OutboundSubFormStore['data'],
    sharedData: Partial<TransportDataV2>,
  ): TransportDataV2 {
    const carrierVisitValue =
      outboundData.carrierVisit === null ? null : outboundData.carrierVisit?.id

    const carrierType = outboundData.carrierType ?? undefined
    const carrierTypeValue =
      outboundData.carrierVisit === null && !carrierType ? CarrierType.Universal : carrierType

    return removeEmptyOptionalFields({
      ...sharedData,
      carrierVisitId: carrierVisitValue,
      carrierType: carrierTypeValue,
      finalDestination: outboundData.finalDestination,
      inlandDestination: outboundData.inlandDestination,
      isApproved: outboundData.approvalForLoading,
    })
  }

  private _mapCargo(cargoData: CargoSubFormStore['data']): CargoDataV2 {
    const { cargoStatus, commodity } = cargoData

    return removeEmptyOptionalFields({
      isEmpty: cargoStatus === 'Empty',
      content: commodity,
      dangerousGoodsDefinitionIds: [],
      imoClasses: [],
      otherLabels: [],
    })
  }

  public _mapDataToCommand(
    formData: RailVisitBulkEditFormStoreType,
    bulkEditData: BulkEditSelectionDto[],
  ): BulkUpdateContainerVisitCommand {
    const containerJourneyIds: number[] = []
    const ordersWithoutJourneyIds: number[] = []

    bulkEditData.forEach(item => {
      if (item.containerJourneyId) {
        containerJourneyIds.push(item.containerJourneyId)
      } else {
        ordersWithoutJourneyIds.push(item.orderId)
      }
    })

    const sharedTransportData = {
      finalDestination: formData.outboundTransport.finalDestination,
      inlandDestination: formData.outboundTransport.inlandDestination,
      placeOfOrigin: formData.inboundTransport.origin,
      portOfLoading: formData.inboundTransport.portOfLoading,
      portOfDischarge: formData.outboundTransport.portOfDischarge,
    }

    const inboundTransportData = this._mapInboundTransport(
      formData.inboundTransport,
      sharedTransportData,
    )
    const outboundTransportData = this._mapOutboundTransport(
      formData.outboundTransport,
      sharedTransportData,
    )

    const cargo = formData.cargo.cargoStatus ? this._mapCargo(formData.cargo) : undefined

    const forwarderValue =
      formData.logistics.forwarder === null ? null : formData.logistics.forwarder?.id
    const shipperValue = formData.logistics.shipper === null ? null : formData.logistics.shipper?.id
    const stowageInstructionValue =
      formData.outboundTransport.stowageInstructions === null
        ? null
        : formData.outboundTransport.stowageInstructions?.id

    const command: BulkUpdateContainerVisitCommand = {
      ids: containerJourneyIds,

      consignee: formData.logistics.consignee,
      forwarderId: forwarderValue,
      shipperId: shipperValue,
      stowageInstructionId: stowageInstructionValue,

      billOfLading: formData.logistics.billOfLadding,
      referenceNumber: formData.logistics.bookingReference,

      inboundTransportData: inboundTransportData,
      outboundTransportData: outboundTransportData,

      inboundCargo: cargo,
      outboundCargo: cargo,

      ordersWithoutJourneyIds: ordersWithoutJourneyIds,
    }

    return removeEmptyOptionalFields(command)
  }
}
